import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { PaginatedResult } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';

const MAX_PICKER_PAGES = 10; // 1000 options — beyond this, show truncation

export interface PickerOptions<T> {
  items: T[];
  /** True when the option universe exceeded the page budget — the UI
   *  must SAY so rather than silently hide valid targets. */
  truncated: boolean;
}

/** Exhaustively page a list endpoint for dropdown options (bounded,
 *  with explicit truncation instead of a silent first-page cap). */
export function usePickerOptions<T extends { id: string }>(
  key: readonly unknown[],
  fetchPage: (
    tenantId: string,
    startAfter: string | undefined
  ) => Promise<PaginatedResult<T>>
): UseQueryResult<PickerOptions<T>> {
  const { tokenReady } = useAuth();
  const { tenantId } = useTenant();
  return useQuery<PickerOptions<T>>({
    queryKey: ['tenant', tenantId, 'picker', ...key],
    enabled: tokenReady && tenantId !== null,
    queryFn: async () => {
      const items: T[] = [];
      let cursor: string | undefined;
      for (let page = 0; page < MAX_PICKER_PAGES; page++) {
        const result = await fetchPage(tenantId as string, cursor);
        items.push(...result.items);
        if (!result.pagination?.hasMore || !result.pagination.nextCursor) {
          return { items, truncated: false };
        }
        cursor = result.pagination.nextCursor;
      }
      return { items, truncated: true };
    },
  });
}
